import React, { useState, useEffect } from 'react';
import { Clock, ArrowRight, Calendar, RotateCcw } from 'lucide-react';

/**
 * Компонент для конвертации времени между различными часовыми поясами
 */
const TimezoneConverter = ({ userTimeZone }) => {
  const [fromDate, setFromDate] = useState(new Date());
  const [fromTime, setFromTime] = useState(formatTimeForInput(new Date()));
  const [fromTimezone, setFromTimezone] = useState(userTimeZone || 'Europe/Moscow');
  const [toTimezone, setToTimezone] = useState('America/New_York');
  const [convertedDate, setConvertedDate] = useState(null);
  const [convertedTime, setConvertedTime] = useState(null);
  const [timezoneDiff, setTimezoneDiff] = useState('');
  const [errors, setErrors] = useState({});

  // Популярные часовые пояса для выбора
  const popularTimezones = [
    { value: 'Europe/Moscow', label: 'Москва (UTC+3)', region: 'Россия' },
    { value: 'Europe/Kaliningrad', label: 'Калининград (UTC+2)', region: 'Россия' },
    { value: 'Asia/Yekaterinburg', label: 'Екатеринбург (UTC+5)', region: 'Россия' },
    { value: 'Asia/Novosibirsk', label: 'Новосибирск (UTC+7)', region: 'Россия' },
    { value: 'Asia/Vladivostok', label: 'Владивосток (UTC+10)', region: 'Россия' },
    { value: 'Asia/Magadan', label: 'Магадан (UTC+11)', region: 'Россия' },
    { value: 'Asia/Kamchatka', label: 'Камчатка (UTC+12)', region: 'Россия' },
    { value: 'America/New_York', label: 'Нью-Йорк (UTC-5/-4)', region: 'США' },
    { value: 'America/Los_Angeles', label: 'Лос-Анджелес (UTC-8/-7)', region: 'США' },
    { value: 'Europe/London', label: 'Лондон (UTC+0/+1)', region: 'Европа' },
    { value: 'Europe/Berlin', label: 'Берлин (UTC+1/+2)', region: 'Европа' },
    { value: 'Europe/Paris', label: 'Париж (UTC+1/+2)', region: 'Европа' },
    { value: 'Asia/Tokyo', label: 'Токио (UTC+9)', region: 'Азия' },
    { value: 'Asia/Shanghai', label: 'Шанхай (UTC+8)', region: 'Азия' },
    { value: 'Asia/Dubai', label: 'Дубай (UTC+4)', region: 'Азия' },
    { value: 'Australia/Sydney', label: 'Сидней (UTC+10/+11)', region: 'Австралия' },
  ];

  // Форматирование времени для элемента ввода
  function formatTimeForInput(date) {
    return date.toTimeString().substring(0, 5); // Формат HH:MM
  }

  // Функция для проверки корректности данных
  function validateInputs() {
    const errors = {};
    
    // Проверка даты
    if (!fromDate) {
      errors.date = 'Укажите дату';
    }
    
    // Проверка времени
    if (!fromTime) {
      errors.time = 'Укажите время';
    } else {
      const timeRegex = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;
      if (!timeRegex.test(fromTime)) {
        errors.time = 'Время должно быть в формате ЧЧ:ММ';
      }
    }
    
    return errors;
  }

  // Расчет разницы между часовыми поясами
  function calculateTimezoneDifference() {
    try {
      // Текущее время
      const now = new Date();
      
      // Определение смещения для первого часового пояса
      const tzFrom = new Date(now.toLocaleString('en-US', { timeZone: fromTimezone }));
      const tzFromOffset = tzFrom.getTime() - now.getTime();
      
      // Определение смещения для второго часового пояса
      const tzTo = new Date(now.toLocaleString('en-US', { timeZone: toTimezone }));
      const tzToOffset = tzTo.getTime() - now.getTime();
      
      // Разница в часах
      const diffInHours = (tzToOffset - tzFromOffset) / (1000 * 60 * 60);
      
      const sign = diffInHours >= 0 ? '+' : '';
      return `${sign}${diffInHours.toFixed(1)} часов`;
    } catch (error) {
      console.error('Ошибка расчета разницы между часовыми поясами:', error);
      return 'Ошибка расчета';
    }
  }

  // Функция конвертации времени
  function convertTime() {
    const validationErrors = validateInputs();
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    
    try {
      // Получаем дату и время из пользовательского ввода
      const [hours, minutes] = fromTime.split(':').map(Number);
      const dateObj = new Date(fromDate);
      dateObj.setHours(hours, minutes, 0, 0);
      
      // Получаем временное смещение исходной зоны
      const fromOffset = new Date(dateObj.toLocaleString('en-US', { timeZone: fromTimezone })).getTime() - 
                         new Date(dateObj.toLocaleString('en-US')).getTime();
      
      // Получаем временное смещение целевой зоны
      const toOffset = new Date(dateObj.toLocaleString('en-US', { timeZone: toTimezone })).getTime() - 
                      new Date(dateObj.toLocaleString('en-US')).getTime();
      
      // Вычисляем разницу между зонами
      const diffInMs = toOffset - fromOffset;
      
      // Применяем разницу к исходному времени
      const convertedDateObj = new Date(dateObj.getTime() + diffInMs);
      
      // Форматируем результат
      const convertedTimeStr = convertedDateObj.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      });
      
      const convertedDateStr = convertedDateObj.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      });
      
      setConvertedTime(convertedTimeStr);
      setConvertedDate(convertedDateStr);
      setTimezoneDiff(calculateTimezoneDifference());
    } catch (error) {
      console.error('Ошибка при конвертации времени:', error);
      setErrors({
        general: 'Произошла ошибка при конвертации времени. Пожалуйста, проверьте введенные данные.'
      });
    }
  }

  // Обрабатываем изменение часовых поясов
  useEffect(() => {
    if (fromTimezone && toTimezone) {
      setTimezoneDiff(calculateTimezoneDifference());
    }
  }, [fromTimezone, toTimezone]);

  // Сброс формы
  const resetForm = () => {
    const now = new Date();
    setFromDate(now);
    setFromTime(formatTimeForInput(now));
    setFromTimezone(userTimeZone || 'Europe/Moscow');
    setToTimezone('America/New_York');
    setConvertedDate(null);
    setConvertedTime(null);
    setErrors({});
  };

  // Группировка часовых поясов по регионам
  const groupedTimezones = popularTimezones.reduce((acc, tz) => {
    if (!acc[tz.region]) {
      acc[tz.region] = [];
    }
    acc[tz.region].push(tz);
    return acc;
  }, {});

  // Поменять местами часовые пояса
  const swapTimezones = () => {
    const temp = fromTimezone;
    setFromTimezone(toTimezone);
    setToTimezone(temp);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center">
        <Clock className="w-5 h-5 mr-2 text-indigo-500" />
        Конвертер часовых поясов
      </h2>

      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-md border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {errors.general}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
        {/* Исходное время */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Исходное время</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Дата
              </label>
              <input
                type="date"
                value={fromDate ? fromDate.toISOString().substr(0, 10) : ''}
                onChange={(e) => setFromDate(new Date(e.target.value))}
                className={`block w-full px-3 py-2 bg-white dark:bg-gray-700 border ${
                  errors.date ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                } rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 dark:text-white`}
              />
              {errors.date && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.date}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Время
              </label>
              <input
                type="time"
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value)}
                className={`block w-full px-3 py-2 bg-white dark:bg-gray-700 border ${
                  errors.time ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                } rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 dark:text-white`}
              />
              {errors.time && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.time}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Часовой пояс
              </label>
              <select
                value={fromTimezone}
                onChange={(e) => setFromTimezone(e.target.value)}
                className="block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 dark:text-white"
              >
                {Object.keys(groupedTimezones).map(region => (
                  <optgroup key={region} label={region}>
                    {groupedTimezones[region].map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Целевое время */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-100 dark:border-green-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center justify-between">
            <span>Целевое время</span>
            {convertedTime && (
              <span className="text-xl font-bold text-green-600 dark:text-green-400">{convertedTime}</span>
            )}
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Часовой пояс
              </label>
              <select
                value={toTimezone}
                onChange={(e) => setToTimezone(e.target.value)}
                className="block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 dark:text-white"
              >
                {Object.keys(groupedTimezones).map(region => (
                  <optgroup key={region} label={region}>
                    {groupedTimezones[region].map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {convertedDate && convertedTime && (
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 mt-2">
                <div className="flex flex-col items-center">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                    {convertedTime}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    {convertedDate}
                  </div>
                  
                  {timezoneDiff && (
                    <div className="mt-3 px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 rounded-full text-xs text-indigo-700 dark:text-indigo-300 font-medium">
                      Разница: {timezoneDiff}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-center mb-4">
        <button 
          type="button"
          onClick={swapTimezones}
          className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
        >
          <ArrowRight className="w-5 h-5 rotate-90" />
        </button>
      </div>

      <div className="flex flex-wrap justify-center gap-4">
        <button
          type="button"
          onClick={convertTime}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors flex items-center"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Конвертировать время
        </button>
        
        <button
          type="button"
          onClick={resetForm}
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg transition-colors flex items-center"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Сбросить
        </button>
      </div>

      <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800">
        <h3 className="font-medium text-gray-900 dark:text-white mb-2">Полезная информация</h3>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc pl-5 space-y-1">
          <li>В России используется постоянное зимнее время (без перехода на летнее)</li>
          <li>В большинстве штатов США и стран Европы используется переход на летнее время</li>
          <li>Переход на летнее время обычно происходит в марте и октябре</li>
          <li>Часовые пояса могут меняться в связи с политическими решениями</li>
        </ul>
      </div>
    </div>
  );
};

export default TimezoneConverter;